const DB_NAME = 'duck-notebook-cache'
const STORE_NAME = 'table-data'
const DB_VERSION = 1

export interface CachedTableData {
  rows: Record<string, string | number>[]
  rowCount: number
  sizeBytes: number
  cachedAt: number
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function makeKey(challengeId: string, tableName: string): string {
  return `${challengeId}:${tableName}`
}

export async function getCachedTable(
  challengeId: string,
  tableName: string,
): Promise<CachedTableData | null> {
  try {
    const db = await openDB()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const request = store.get(makeKey(challengeId, tableName))
      request.onsuccess = () => resolve(request.result ?? null)
      request.onerror = () => reject(request.error)
    })
  } catch {
    return null
  }
}

export async function getCachedTablesForChallenge(
  challengeId: string,
  tableNames: string[],
): Promise<Map<string, CachedTableData>> {
  const result = new Map<string, CachedTableData>()
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)

    await Promise.all(
      tableNames.map(
        (name) =>
          new Promise<void>((resolve) => {
            const request = store.get(makeKey(challengeId, name))
            request.onsuccess = () => {
              if (request.result) result.set(name, request.result)
              resolve()
            }
            request.onerror = () => resolve()
          }),
      ),
    )
  } catch {
    // Silently return whatever we have
  }
  return result
}

export async function cacheTable(
  challengeId: string,
  tableName: string,
  data: CachedTableData,
): Promise<void> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    store.put(data, makeKey(challengeId, tableName))
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch (err) {
    console.error('Failed to cache table data:', err)
  }
}

export async function clearChallengeCache(challengeId: string): Promise<void> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)

    // Iterate all keys and delete ones matching this challengeId
    const request = store.openCursor()
    request.onsuccess = () => {
      const cursor = request.result
      if (cursor) {
        if (typeof cursor.key === 'string' && cursor.key.startsWith(`${challengeId}:`)) {
          cursor.delete()
        }
        cursor.continue()
      }
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch (err) {
    console.error('Failed to clear challenge cache:', err)
  }
}
